<template>
  <div id="app" class="app-container">
    <header class="app-header">
      <a href="#main" class="sr-only sr-only-focusable" >Skip to main content</a>
      <AppNavBar />
      <div v-show="showSessionReminder">
        <div class="border rounded p-2 bg-warning d-flex justify-content-center align-items-center" >
          <div>
            <strong>Session Timeout: </strong><span class="mx-2">Your session will timout in</span>      
          </div>
          <strong><Vidle @idle="onIdle"
                         @remind="onRemind"
                         @refresh="onRefresh"
                         :duration=timeoutDuration
                         :reminders=reminders /> 
          </strong>                     
        </div>        
      </div>
    </header>
    <main id="main" role="main" class="app-content">
      <Toast group="top-toast" position="top-center"/>
      <MnConfirmDialog />
      <MnAlertDialog />
      <RouterView />

    </main>
    <Teleport to="body">
      <MnLoader :visible="loadingState.loading" :message="loadingState.loadingMessage" /> 
    </Teleport>           
  </div>
</template>

<script setup>
// ===============================
// IMPORTS
// ===============================
// Vue Core
import { ref, onMounted, onUnmounted, watch } from 'vue';
import { RouterView } from 'vue-router';

// Components
import AppNavBar from '@/components/menu/AppNavBar.vue';
import Vidle from 'v-idle';
import Toast from 'primevue/toast';
import MnAlertDialog from './components/common/MnAlertDialog.vue';
import MnLoader from './components/common/MnLoader.vue';
import MnConfirmDialog from './components/common/MnConfirmDialog.vue';

// Composables


// Stores
import { useAuthStore } from './stores/authStore';
import { useAppStore } from './stores/appStore';

// Constants and Utils


// ===============================
// PROPS & EMITS
// ===============================


// ===============================
// COMPOSABLES & STORES
// ===============================
const authStore = useAuthStore();
const appStore = useAppStore();
const { loadingState } = appStore;

// ===============================
// REACTIVE STATE
// ===============================


// ===============================
// CONSTANTS
// ===============================
// User is only reminded 60 seconds before the session expires.
const reminders = [60];
// The session timeout duration is set to 20 minutes.
const timeoutDuration =  parseInt(import.meta.env.VITE_APP_TIMEOUT);
const showSessionReminder = ref(false);
const hasUserResponded = ref(false);
let tokenRefreshInterval = null;


// ===============================
// COMPUTED PROPERTIES
// ===============================


// ===============================
// METHODS
// ===============================

/**
 * Executes the specified callback function when the user is idle.
 * 
 * @param {Function} callback - The callback function to execute when the user is idle.
 * @returns {void}
 */
const onIdle = () => {
  if (authStore.authenticated) {
    authStore.logout();
    localStorage.removeItem('txStore');
  }
};
/**
 * Handles the onRemind event.
 * This function sends a reminder to the user.
 * @param {string} message - The reminder message.
 * @returns {void}
 */
const onRemind = () => {
  if (authStore.authenticated) {
    showSessionReminder.value = true;
    hasUserResponded.value = false;
  }
};

/**
 * Handles the onRefresh event.
 * This function refreshes the data on the page.
 * @returns {void}
 */
const onRefresh = async () => {
  if (!hasUserResponded.value && showSessionReminder.value) {
    hasUserResponded.value = true;
    showSessionReminder.value = false;
    stopTokenRefreshInterval();
    if (authStore.authenticated) {
      try {
        await authStore.refreshUserToken();
      } catch (error) {
        authStore.logout();
      }
    }
  }
};

const startTokenRefreshInterval = () => {
  // Clear any existing interval
  stopTokenRefreshInterval();

  const refreshInterval = (timeoutDuration - 60) * 1000;
  // Refresh token every 4 minutes (240 seconds)
  // This is 1 minute before the 5-minute token expiration
  tokenRefreshInterval = setInterval(async () => {
    if (authStore.authenticated && !showSessionReminder.value && authStore.isTokenExpired) {
      try {
        await authStore.refreshUserToken();
      } catch (error) {
        console.error('Auto token refresh failed:', error);
        // Let the idle timeout handle logout
      }
    }
  }, refreshInterval);
};

const stopTokenRefreshInterval = () => {
  if (tokenRefreshInterval) {
    clearInterval(tokenRefreshInterval);
    tokenRefreshInterval = null;
  }
};

// ===============================
// WATCHERS
// ===============================


// ===============================
// LIFECYCLE HOOKS
// ===============================
onMounted(() => {
  watch(
    () => authStore.authenticated,
    async (authenticated) => {
      if (authenticated) {
        try {
          await appStore.initializeStore();
          // Start proactive token refresh
          startTokenRefreshInterval();
        } catch (error) {
          console.error('Error initializing app store:', error);
        }
      } else {
        // Stop proactive token refresh when not authenticated
        stopTokenRefreshInterval();
      }
    },
    { immediate: true } // Triggers the watcher immediately upon setup
  );

});

onUnmounted(() => {
  stopTokenRefreshInterval();
});

// ===============================
// EXPOSE
// ===============================

</script>
<style>
.app-container {
  display: flex;
  flex-direction: column;
  height: 100vh;
}

.app-header {
  flex-shrink: 0;
}

.app-content {
  padding: 1rem;
  flex-grow: 1;
  overflow-y: auto;
}
</style>



